import {AppOptions, Language, LanguageKey, WordEntry, WordIndexKey} from 'types/db';
import {KEY_DELIMITER} from 'utils/constants';
import {v4 as uuidv4} from 'uuid';

const defaultAppOptions: AppOptions = {
  appLanguage: Language.ENGLISH,
  matchWordsPerRound: 8,
  perQuestionAllowedTimeInSec: 0,
  primaryLanguage: Language.ENGLISH,
  secondaryLanguage: Language.HINDI,
  // Slightly slower than normal speech so learners catch every syllable.
  speechRate: 0.85,
};

class Database {
  database: Record<string, any> = {};

  wordIndex: Record<WordIndexKey, string> = {};

  appOptions: AppOptions = defaultAppOptions;

  public get(key: string): any {
    return this.database[key];
  }

  public set(key: string, value: any): void {
    this.database[key] = value;
  }

  public delete(idToDelete: string): void {
    delete this.database[idToDelete];
    this.wordIndex = Object.fromEntries(Object.entries(this.wordIndex).filter(([, value]) => value !== idToDelete));
  }

  public clearDB(): void {
    this.database = {};
    this.wordIndex = {};
    this.appOptions = defaultAppOptions;
  }

  public getState(): void {
    console.log('💿', this.database);
    console.log('💿', this.wordIndex);
    console.log('💿', this.appOptions);
  }
}

export class EntumanyDB extends Database {
  private static instance: EntumanyDB;

  public constructor() {
    super();
    this.populateFromLocalStorage();
  }

  private getWordIndex(e: WordEntry): WordIndexKey {
    return `${e.word.toLowerCase()}${KEY_DELIMITER}${e.language}`;
  }

  private generateId(): string {
    return uuidv4();
  }

  private mergeEntries(idA: string, idB: string): void {
    const newId = this.generateId();

    this.database[newId] = {...this.database[idA], ...this.database[idB]};
    delete this.database[idA];
    delete this.database[idB];

    Object.entries<any>(this.database[newId]).map(([language, word]) => {
      const idx = this.getWordIndex({language: language as Language, word});
      this.wordIndex[idx] = newId;
    });
  }

  public static getInstance(): EntumanyDB {
    if (!EntumanyDB.instance) {
      EntumanyDB.instance = new EntumanyDB();
    }

    return EntumanyDB.instance;
  }

  public saveToLocalStorage(): void {
    localStorage.setItem('database', JSON.stringify(this.database));
    localStorage.setItem('wordIndex', JSON.stringify(this.wordIndex));
    localStorage.setItem('appOptions', JSON.stringify(this.appOptions));
  }

  public populateFromLocalStorage(): void {
    this.database = JSON.parse(localStorage.getItem('database') || '{}');
    this.wordIndex = JSON.parse(localStorage.getItem('wordIndex') || '{}');
    // Merge over defaults so every option is always defined, even on a fresh
    // install or after a partial/older backup is restored.
    this.appOptions = {...defaultAppOptions, ...JSON.parse(localStorage.getItem('appOptions') || '{}')};
  }

  public addWords(a: WordEntry, b: WordEntry): void {
    const aIndex = this.getWordIndex(a); // For eg: "hello|||en"
    const bIndex = this.getWordIndex(b); // For eg: "Hellu|||de"

    const idA = this.wordIndex[aIndex];
    const idB = this.wordIndex[bIndex];

    if (idA && idB) {
      this.mergeEntries(idA, idB);
      this.saveToLocalStorage();
      return;
    }

    const id = idA || idB || this.generateId();

    if (!this.database[id]) {
      this.database[id] = {};
    }

    this.database[id][a.language] = a.word;
    this.database[id][b.language] = b.word;
    this.wordIndex[aIndex] = id;
    this.wordIndex[bIndex] = id;

    // Persist immediately. Relying on the beforeunload/unmount handlers alone is
    // unsafe on mobile, where backgrounded tabs are often discarded without
    // firing those events — freshly added words would silently vanish.
    this.saveToLocalStorage();
  }

  /**
   * Replace the words of an existing entry, one per language, rebuilding the
   * word index for that entry. A language whose word is blanked out is dropped
   * from the set.
   */
  public updateWordEntry(id: string, entries: WordEntry[]): void {
    if (!this.database[id]) return;

    // Drop every index key pointing at this entry; we rebuild them below.
    this.wordIndex = Object.fromEntries(Object.entries(this.wordIndex).filter(([, value]) => value !== id));

    const updated: Record<string, string> = {};
    entries.forEach(({language, word}) => {
      const trimmed = word.trim();
      if (!trimmed) return;
      updated[language] = trimmed;
      this.wordIndex[this.getWordIndex({language, word: trimmed})] = id;
    });

    this.database[id] = updated;
    this.saveToLocalStorage();
  }

  public deleteWordEntryWithIndex(id: string): void {
    this.delete(id);
    this.saveToLocalStorage();
  }

  public updateLanguage(id: LanguageKey, newLanguage: Language): void {
    this.appOptions[id] = newLanguage;
    this.saveToLocalStorage();
  }

  public updateAppLanguage(newLanguage: Language): void {
    this.appOptions.appLanguage = newLanguage;
    // this one needs instant save
    this.saveToLocalStorage();
  }

  public updatePerQuestionAllowedTimeInSec(newPerQuestionAllowedTimeInSec = 0): void {
    this.appOptions.perQuestionAllowedTimeInSec = newPerQuestionAllowedTimeInSec;
  }

  public updateSpeechRate(newRate: number): void {
    this.appOptions.speechRate = newRate;
    this.saveToLocalStorage();
  }

  public updateMatchWordsPerRound(newCount: number): void {
    this.appOptions.matchWordsPerRound = newCount;
    this.saveToLocalStorage();
  }

  public getExistingWords(language: Language): string[] {
    const words: string[] = [];
    Object.values(this.database).forEach((entry: any) => {
      if (entry[language]) {
        words.push(entry[language]);
      }
    });
    return words;
  }

  public getWordSuggestions(input: string, language: Language, maxSuggestions = 5): string[] {
    if (!input.trim()) return [];

    const existingWords = this.getExistingWords(language);
    const inputLower = input.toLowerCase();

    return existingWords
      .filter((word) => word.toLowerCase().includes(inputLower) && word.toLowerCase() !== inputLower)
      .slice(0, maxSuggestions);
  }
}
